/**
 * Modal — a reusable, accessible, on-brand dialog used by every editable panel
 * for its add/edit forms (the house rule: edits open in a stylized modal, not an
 * inline expanded row).
 *
 * A centered card floats over a dimmed backdrop. Header carries the title + a
 * close ✕; the body scrolls when tall; the footer holds Guardar / Cancelar.
 *
 * Accessibility:
 *  - `role="dialog"` + `aria-modal` + `aria-labelledby` (the header title).
 *  - Focus moves into the dialog on open and is trapped inside it (Tab/Shift-Tab
 *    cycle the focusable controls); on close focus returns to the element that
 *    was focused when it opened.
 *  - Escape closes; a click on the backdrop (not the card) closes.
 *
 * Presentation-only: the caller renders the form fields as `children` and wires
 * the footer via `onConfirm` (Guardar) / `onClose` (Cancelar / ✕ / Escape /
 * backdrop). Pass `busy` to disable the footer while a mutation is in flight,
 * and `canConfirm={false}` to keep Guardar disabled until the form is valid.
 */
import { useCallback, useEffect, useRef } from "react";
import type { ReactNode } from "react";

export interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onConfirm?: () => void;
  children: ReactNode;
  /** Footer Guardar label (default from the caller — usually t("…save")). */
  confirmLabel?: string;
  /** Footer Cancelar label. */
  cancelLabel?: string;
  /** Disable both footer buttons while a write is in flight. */
  busy?: boolean;
  /** Gate the Guardar button (e.g. required field empty). Defaults to true. */
  canConfirm?: boolean;
  /** Accessible label for the ✕ close control. */
  closeLabel?: string;
  /** Optional extra content pinned to the footer's left (e.g. an error line). */
  footerNote?: ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  title,
  onClose,
  onConfirm,
  children,
  confirmLabel = "Guardar",
  cancelLabel = "Cancelar",
  busy = false,
  canConfirm = true,
  closeLabel = "Cerrar",
  footerNote,
}: ModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  // Remember what had focus so we can restore it on close.
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2)}`).current;

  // Move focus into the dialog when it opens; restore it when it closes.
  useEffect(() => {
    if (!open) return;
    restoreRef.current = (document.activeElement as HTMLElement) ?? null;
    // Focus the first focusable control (or the card itself as a fallback).
    const card = cardRef.current;
    const first = card?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? card)?.focus();
    return () => {
      restoreRef.current?.focus?.();
    };
  }, [open]);

  // Escape closes; Tab is trapped within the dialog.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const card = cardRef.current;
      if (!card) return;
      const nodes = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (n) => n.offsetParent !== null || n === document.activeElement,
      );
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = nodes[0]!;
      const lastEl = nodes[nodes.length - 1]!;
      const active = document.activeElement as HTMLElement;
      if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      // A click that starts and ends on the backdrop (not the card) closes.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={cardRef}
        onKeyDown={onKeyDown}
      >
        <div className="modal-head">
          <h3 id={titleId} className="modal-title">
            {title}
          </h3>
          <button
            type="button"
            className="modal-close"
            aria-label={closeLabel}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="modal-body">{children}</div>

        <div className="modal-foot">
          <div className="modal-foot-note">{footerNote}</div>
          <div className="modal-foot-actions">
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={onClose}
            >
              {cancelLabel}
            </button>
            {onConfirm ? (
              <button
                type="button"
                className="btn primary"
                disabled={busy || !canConfirm}
                onClick={onConfirm}
              >
                {confirmLabel}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
